import React, { useCallback, useEffect, useState } from 'react';
import { Modal, Radio, Spin, Empty } from 'antd';
import { MergeCellsOutlined, ScanOutlined, SwapOutlined } from '@ant-design/icons';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import config from '../../config';
import {
  NdoePage, NdoePageHeader, NdoeActionButton, NdoeImage, NdoeLoading,
} from '../../components/ndoe';
import './fusionDoublons.css';

const DOMAIN = config.REACT_APP_SERVER_DOMAIN;

// Menu admin « Fusion de doublons » (18/07). Détecte des fiches produit en double
// (même nom+marque, ou photos proches au sein d'une même marque+catégorie), montre
// les DEUX photos côte à côte, laisse l'admin choisir la fiche à garder, affiche un
// APERÇU (ce qui va bouger) puis fusionne — le serveur re-rattache tout et prouve
// zéro orphelin. L'image aide à décider, elle ne décide jamais seule.
const FusionDoublons = () => {
  const user = useSelector((s) => s.user?.currentUser);
  const [groupes, setGroupes] = useState([]);
  const [imgCandidats, setImgCandidats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanImg, setScanImg] = useState(false);
  // Choix de la fiche à garder, par candidat (clé = ids joints).
  const [garde, setGarde] = useState({});
  const [apercu, setApercu] = useState(null);       // { paire, data, enCours }
  const [fusionEnCours, setFusionEnCours] = useState(false);

  const charger = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${DOMAIN}/api/fusion/doublons`);
      setGroupes(data.groupes || []);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Chargement impossible.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const chercherImage = async () => {
    setScanImg(true);
    try {
      const { data } = await axios.get(`${DOMAIN}/api/fusion/candidats-image`);
      setImgCandidats(data.candidats || []);
      toast.info(`${(data.candidats || []).length} paire(s) proche(s) par l'image (${data.analyses} modèles analysés). À confirmer à l'œil.`);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Analyse d’image impossible.');
    } finally { setScanImg(false); }
  };

  // Un candidat = 2 produits. Renvoie { gardeId, fusionneId } selon le choix.
  const paireDe = (cand, cle) => {
    const ids = cand.produits.map((p) => p.id_produit);
    const gardeId = garde[cle] || ids[0];
    const fusionneId = ids.find((x) => x !== gardeId);
    return { gardeId, fusionneId };
  };

  const previsualiser = async (cand, cle) => {
    const { gardeId, fusionneId } = paireDe(cand, cle);
    setApercu({ cand, cle, enCours: true, data: null });
    try {
      const { data } = await axios.get(`${DOMAIN}/api/fusion/apercu`, { params: { garde: gardeId, fusionne: fusionneId } });
      setApercu({ cand, cle, enCours: false, data });
    } catch (e) {
      setApercu(null);
      toast.error(e.response?.data?.error || 'Aperçu impossible.');
    }
  };

  const confirmer = async () => {
    if (!apercu?.data) return;
    const { gardeId, fusionneId } = paireDe(apercu.cand, apercu.cle);
    setFusionEnCours(true);
    try {
      const { data } = await axios.post(`${DOMAIN}/api/fusion/appliquer`, { garde: gardeId, fusionne: fusionneId });
      toast.success(`Fusion effectuée (${data.bilan.reparentees} variante(s) re-rattachée(s), ${data.bilan.collisions} fusionnée(s), 0 orphelin).`);
      setApercu(null);
      // Retire la paire fusionnée des listes.
      setGroupes((prev) => prev.filter((g) => g !== apercu.cand));
      setImgCandidats((prev) => prev.filter((g) => g !== apercu.cand));
    } catch (e) {
      toast.error(e.response?.data?.error || 'Fusion impossible.');
    } finally { setFusionEnCours(false); }
  };

  const renderCandidat = (cand, cle) => {
    const ids = cand.produits.map((p) => p.id_produit);
    const gardeId = garde[cle] || ids[0];
    return (
      <div className="fus-carte" key={cle}>
        <div className="fus-carte-tete">
          {cand.source === 'image'
            ? <span className="fus-motif fus-motif--img"><ScanOutlined /> Photos proches (distance {cand.distance}) · {cand.marque} — à confirmer</span>
            : <span className="fus-motif fus-motif--nom"><SwapOutlined /> Même nom + marque</span>}
        </div>
        <Radio.Group value={gardeId} onChange={(e) => setGarde((prev) => ({ ...prev, [cle]: e.target.value }))} className="fus-paire">
          {cand.produits.map((p) => (
            <label className={`fus-produit ${gardeId === p.id_produit ? 'is-garde' : 'is-fusionne'}`} key={p.id_produit}>
              <div className="fus-photo"><NdoeImage src={p.img} alt={p.nom_produit || p.code_produit} size="lg" preview={false} /></div>
              <div className="fus-infos">
                <div className="fus-code">{p.code_produit}</div>
                <div className="fus-nom">{p.nom_produit || '—'}</div>
                <Radio value={p.id_produit}>{gardeId === p.id_produit ? 'À GARDER' : 'à fusionner'}</Radio>
              </div>
            </label>
          ))}
        </Radio.Group>
        <div className="fus-actions">
          <NdoeActionButton variant="primary" icon={<MergeCellsOutlined />} onClick={() => previsualiser(cand, cle)}>
            Prévisualiser la fusion
          </NdoeActionButton>
        </div>
      </div>
    );
  };

  if (user?.role !== 'admin') {
    return <NdoePage><Empty description="Réservé aux administrateurs." /></NdoePage>;
  }

  return (
    <NdoePage>
      <NdoePageHeader
        eyebrow="Administration"
        title="Fusion de doublons"
        subtitle="Deux fiches pour le même modèle ? Choisissez celle à garder — tout ce qui pointait l'autre (ventes, stock, livraisons, photos) est re-rattaché, sans rien perdre."
      />

      <div className="fus-barre">
        <NdoeActionButton variant="secondary" icon={<ScanOutlined />} loading={scanImg} onClick={chercherImage}>
          Chercher aussi par la photo
        </NdoeActionButton>
        <span className="fus-hint">Le nom + la marque détecte les doublons évidents ; la photo remonte ceux au nom différent (à vérifier à l'œil).</span>
      </div>

      {loading ? (
        <NdoeLoading rows={4} />
      ) : (
        <>
          {imgCandidats.length > 0 && (
            <section className="fus-section">
              <h3 className="fus-titre">Proches par la photo — <b>à confirmer</b> ({imgCandidats.length})</h3>
              {imgCandidats.map((c, i) => renderCandidat(c, `img-${c.produits.map((p) => p.id_produit).join('-')}-${i}`))}
            </section>
          )}
          <section className="fus-section">
            <h3 className="fus-titre">Même nom + marque ({groupes.length})</h3>
            {groupes.length === 0
              ? <Empty description="Aucun doublon par nom + marque." />
              : groupes.map((g) => renderCandidat(g, `nom-${g.produits.map((p) => p.id_produit).join('-')}`))}
          </section>
        </>
      )}

      <Modal
        open={!!apercu}
        onCancel={() => setApercu(null)}
        title="Aperçu de la fusion"
        okText="Confirmer la fusion"
        cancelText="Annuler"
        onOk={confirmer}
        confirmLoading={fusionEnCours}
        okButtonProps={{ disabled: apercu?.enCours || !apercu?.data }}
        width={520}
        destroyOnClose
      >
        {apercu?.enCours ? (
          <div style={{ textAlign: 'center', padding: 20 }}><Spin /> <span>Calcul de l'aperçu…</span></div>
        ) : apercu?.data ? (
          <div className="fus-apercu">
            <p>
              On garde <b>{apercu.data.garde.code_produit}</b> et on archive <b>{apercu.data.fusionne.code_produit}</b>.
            </p>
            <ul>
              <li><b>{apercu.data.variantes.fusionne}</b> variante(s) de la fiche archivée : {apercu.data.variantes.reparentees} re-rattachée(s), {apercu.data.variantes.collisions} fusionnée(s) avec une variante existante.</li>
              <li>Stock cumulé sur la fiche gardée : <b>+{apercu.data.stock_a_cumuler}</b> paire(s).</li>
              {Object.keys(apercu.data.lignes_a_repointer || {}).length > 0 ? (
                <li>Rattachées à la fiche gardée :
                  <ul>
                    {Object.entries(apercu.data.lignes_a_repointer).map(([k, v]) => (
                      <li key={k}>{libelle(k)} : <b>{v}</b></li>
                    ))}
                  </ul>
                </li>
              ) : <li>Aucune vente/mouvement rattaché.</li>}
            </ul>
            <p className="fus-note">La fiche archivée n'est jamais supprimée (l'historique reste intact). L'opération se fait en un seul bloc, avec vérification « zéro orphelin ».</p>
          </div>
        ) : null}
      </Modal>
    </NdoePage>
  );
};

// Traduit une clé technique table.colonne en libellé lisible.
function libelle(cle) {
  const map = {
    'detail_commande.id_varianteProduit': 'Ventes / lignes de commande',
    'detail_livraison.id_varianteProduit': 'Livraisons',
    'detail_livraison.id_variante_retour': 'Retours de livraison',
    'mouvement_stock.id_varianteProduit': 'Mouvements de stock',
    'reception.id_varianteProduit': 'Réceptions',
    'reservation.id_varianteProduit': 'Réservations',
    'produit_photo_partage.id_produit': 'Photos de partage',
    'promotion.id_produit': 'Promotions',
  };
  return map[cle] || cle;
}

export default FusionDoublons;
